/**
 * Plus Subscription Required Component
 *
 * This component is shown when users try to access Plus-only features
 * like Predictive Spend without having a Plus subscription.
 */

import { useUserStore } from "@/stores/user-store";
import { ArrowLeftOutlined, CrownOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Space, Typography } from "antd";
import React from "react";
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;

interface PlusSubscriptionRequiredProps {
	featureName?: string;
}

export const PlusSubscriptionRequired: React.FC<
	PlusSubscriptionRequiredProps
> = ({ featureName = "this feature" }) => {
	const navigate = useNavigate();
	const user = useUserStore((state) => state.user);

	// Super admins (access_level >= 1000) should never see this block
	// Check both snake_case and camelCase versions for compatibility
	const accessLevel = user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
	const isSuperAdmin = accessLevel >= 1000;
	if (isSuperAdmin) {
		return null;
	}

	const handleUpgrade = () => {
		navigate("/subscription");
	};

	const handleGoBack = () => {
		navigate(-1);
	};

	return (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				minHeight: "60vh",
				padding: "24px",
			}}
		>
			<Card
				style={{
					maxWidth: "600px",
					width: "100%",
					textAlign: "center",
				}}
				bordered={false}
			>
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					<div>
						<CrownOutlined
							style={{
								fontSize: "64px",
								color: "#faad14",
								marginBottom: "16px",
							}}
						/>
						<Title level={2}>Plus Subscription Required</Title>
					</div>

					<Alert
						message={`${featureName} is a Plus feature`}
						description="Upgrade to FacilityAI Plus to unlock advanced features like Predictive Spend analytics."
						type="info"
						showIcon
						style={{ marginBottom: "24px" }}
					/>

					<div>
						<Paragraph>
							<Text strong>FacilityAI Plus includes:</Text>
						</Paragraph>
						<div
							style={{ textAlign: "left", maxWidth: "400px", margin: "0 auto" }}
						>
							<ul style={{ paddingLeft: "20px" }}>
								<li>SlimForm AI-Powered Asset Capture</li>
								<li>Predictive Spend Analytics</li>
								<li>Advanced Asset Management</li>
								<li>Enhanced Reporting</li>
								<li>Priority Support</li>
								<li>All Base features included</li>
							</ul>
						</div>
					</div>

					<Space size="middle">
						<Button
							size="large"
							icon={<ArrowLeftOutlined />}
							onClick={handleGoBack}
						>
							Go Back
						</Button>
						<Button
							type="primary"
							size="large"
							icon={<CrownOutlined />}
							onClick={handleUpgrade}
						>
							Upgrade to Plus
						</Button>
					</Space>

					<div style={{ marginTop: "24px" }}>
						<Text type="secondary" style={{ fontSize: "14px" }}>
							Questions about upgrading? Contact our support team for
							assistance.
						</Text>
					</div>
				</Space>
			</Card>
		</div>
	);
};
